import {MithrilComponent} from '@bitstillery/mithril'
import {Button, ButtonGroup, Dialog, FieldSelect, FieldText, RadioGroup} from '@bitstillery/common/components/portal'
import {state, type State} from '@bitstillery/mithril'
import {set_language} from '@bitstillery/common/lib/i18n'
import {supported_languages} from '@bitstillery/common/lib/i18n'
import {$t, api, notifier} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import {and, email, required, validation} from '@bitstillery/common/lib/validation'
import type {UpdatePortalProfileRequest} from '@bitstillery/common/api/fact2server'

import {$m, $s} from '@/app'
import {load_user} from '@/models/identity'

interface MyPortalSettingsData {
    delete_account_request: {
        email_address: string
        name: string
    }
    deleting: boolean
    deleting_success: boolean
    timezones: {value: string; label: string}[]
}

export class MyPortalSettings extends MithrilComponent {
    pricePreferenceOptions = [
        // @i18n include: product.bottles
        // @i18n include: product.cases
        ['bottle', 'product.bottles'],
        ['case', 'product.cases'],
    ]

    $v = {
        email_address: validation($s.identity.user.$email_address, and([required(), email()])),
        first_name: validation($s.identity.user.$first_name, required()),
        last_name: validation($s.identity.user.$last_name, required()),
    }

    data: State<MyPortalSettingsData> = state(
        {
            delete_account_request: {
                email_address: '',
                name: '',
            },
            deleting: false,
            deleting_success: false,
            timezones: [],
        },
        'my-portal-preferences-data',
    )

    override async oninit() {
        // @i18n include: shell.title.my_portal_settings
        // @i18n include: shell.description.my_portal_settings
        const {result: timezones, success} = await api.get('portal/data/timezones')
        if (!success) {
            notifier.notify('Cannot load the timezones', 'warning')
            return
        }
        this.data.timezones.splice(0, this.data.timezones.length, ...timezones.map((i) => ({label: i, value: i})))
    }

    async request_removal() {
        const data = {
            new_contact_person_contactdata: this.data.delete_account_request.email_address,
            new_contact_person_name: this.data.delete_account_request.name,
        }

        const {success} = await api.post('portal/user/send-deletion-request-email', data, true)
        if (success) {
            this.data.deleting = false
            this.data.deleting_success = true
        }
    }

    async update_profile() {
        const payload: UpdatePortalProfileRequest = {
            email_address: $s.identity.user.email_address,
            first_name: $s.identity.user.first_name ?? '',
            language: $s.identity.user.language,
            last_name: $s.identity.user.last_name ?? null,
            mobile_phone_number: $s.identity.user.mobile_phone_number ?? null,
            phone_number: $s.identity.user.phone_number ?? null,
            price_preference: $s.identity.user.price_preference,
            timezone: $s.identity.user.timezone,
        }
        const {response} = await api.post('portal/user/profile', payload, true)
        if (!response.ok) {
            notifier.notify($t('my_portal.preferences.saved_profile_error'), 'warning')
            return
        }

        await load_user()
        void set_language($s.language)
        await $m.order.load_current_order()
        notifier.notify($t('my_portal.preferences.saved_profile'), 'info')
    }

    view(_vn: m.Vnode) {
        return (
            <div className='c-my-portal-settings'>
                {this.data.deleting && (
                    <Dialog onclose={() => (this.data.deleting = false)} title={$t('profile.removal_title')}>
                        <h3>{$t('profile.removal_notice')}</h3>
                        <p>{$t('profile.removal_description_1')}</p>
                        <p>{$t('profile.removal_description_2')}</p>

                        <div className='form'>
                            <FieldText label={$t('profile.replacement.name')} model={this.data.delete_account_request.$name} />
                            <FieldText
                                label={$t('profile.replacement.contact')}
                                model={this.data.delete_account_request.$email_address}
                            />
                        </div>

                        <p>
                            {$t('profile.removal_questions', {
                                email: $s.identity.user.sales_manager.email_address,
                                email_vendor: process.env.MSI_THEME === 'msp' ? 'info@movingspirits.nl' : 'info@a2bc.com',
                                manager: $s.identity.user.sales_manager.name,
                                phone: $s.identity.user.sales_manager.phone_number,
                            })}
                        </p>
                        <ButtonGroup>
                            <Button
                                icon='cancel'
                                type='default'
                                text={$t('profile.removal.cancel')}
                                onclick={() => (this.data.deleting = false)}
                            />
                            <Button
                                icon='trash'
                                type='danger'
                                text={$t('profile.removal.confirm')}
                                onclick={this.request_removal.bind(this)}
                            />
                        </ButtonGroup>
                    </Dialog>
                )}

                {this.data.deleting_success && (
                    <Dialog title={$t('profile.removal.ack')} onclose={() => (this.data.deleting_success = false)}>
                        <div className='form-horizontal'>
                            <p>
                                {$t('profile.removal.ack_description_1')}
                                {$t('profile.removal.ack_description_2', {
                                    email: $s.identity.user.sales_manager.email_address,
                                    email_vendor: process.env.MSI_THEME === 'msp' ? 'info@movingspirits.nl' : 'info@a2bc.com',
                                    manager: $s.identity.user.sales_manager.name,
                                    phone: $s.identity.user.sales_manager.phone_number,
                                })}
                            </p>
                            <ButtonGroup>
                                <Button
                                    icon='cancel'
                                    onclick={() => (this.data.deleting_success = false)}
                                    text={$t('profile.removal.close_window')}
                                    type='default'
                                />
                            </ButtonGroup>
                        </div>
                    </Dialog>
                )}

                <h3 className='tile-container-title'>{$t('my_portal.options.settings')}</h3>
                <div className='fieldset-group'>
                    <div className='fieldset'>
                        <FieldText disabled={true} label={$t('profile.company')} model={$s.identity.user.$relation_name} />
                        <div className='field-group'>
                            <FieldText
                                label={$t('profile.first_name')}
                                model={$s.identity.user.$first_name}
                                validation={this.$v.first_name}
                            />
                            <FieldText
                                label={$t('profile.family_name')}
                                model={$s.identity.user.$last_name}
                                validation={this.$v.last_name}
                            />
                        </div>
                        <div className='field-group'>
                            <FieldText label={$t('profile.phone_number')} model={$s.identity.user.$phone_number} />
                            <FieldText label={$t('profile.mobile_phone_number')} model={$s.identity.user.$mobile_phone_number} />
                        </div>
                        <FieldText
                            label={$t('profile.email')}
                            model={$s.identity.user.$email_address}
                            validation={this.$v.email_address}
                        />
                    </div>
                </div>

                <div className='fieldset'>
                    <FieldSelect
                        help={$t('my_portal.preferences.language_help')}
                        label={$t('my_portal.preferences.language')}
                        model={$s.identity.user.$language}
                        onchange={(language_code: string) => {
                            void set_language(language_code)
                        }}
                        options={supported_languages.map(([value, label]) => ({label, value}))}
                    />

                    <FieldSelect
                        help={$t('my_portal.preferences.timezone_help')}
                        label={$t('my_portal.preferences.timezone')}
                        model={$s.identity.user.$timezone}
                        options={this.data.timezones}
                    />

                    <RadioGroup
                        help={$t('my_portal.preferences.price_quantity_help')}
                        label={$t('my_portal.preferences.price_quantity')}
                        model={$s.identity.user.$price_preference}
                        options={this.pricePreferenceOptions}
                        translate={{prefix: ''}}
                    />
                    <Button
                        className='btn-submit'
                        icon='save'
                        onclick={this.update_profile.bind(this)}
                        text={$t('my_portal.preferences.save_button')}
                        type='info'
                    />
                </div>

                <div className='danger-zone fieldset'>
                    <Button
                        className='btn-removal'
                        icon='profileCancel'
                        onclick={() => (this.data.deleting = true)}
                        text={$t('profile.request_removal')}
                        type='danger'
                        variant='link'
                    />
                </div>
            </div>
        )
    }
}
